import styled from 'styled-components';
import React, {FC} from 'react';
import {Link} from 'gatsby';

import {paletteR4, fontFamiliesR4} from '../../../../lib/styleVariablesR4';
import {
  fontWeights,
  mediaBreakpointDown,
  mediaBreakpointOnly,
  transition
} from '../../../../lib/styleVariables';
import {convertHexToRGBA} from '../../../../lib/color-utils';
import {headerHeight} from './headerVariables';

export interface Item {
  name: string;
  href: string;
}

interface OverlayMenuProps {
  items: Item[];
  isVisible: boolean;
  topBarHeight: number;
}

const StyledWrapper = styled.div<{isVisible: boolean; topBarHeight: number}>`
  width: 100%;
  height: ${props => `calc(100vh - ${headerHeight.desktop + props.topBarHeight}px)`};
  background: ${paletteR4.white.base};
  padding: 20px;
  position: fixed;
  bottom: 0;
  left: 0;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity ${transition.fast};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};

  &::after {
    width: 80vh;
    height: 80vh;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      ${convertHexToRGBA(paletteR4.green.tint!, 0)} 37.13%,
      ${paletteR4.green.tint} 91.38%
    );
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
  }

  ${mediaBreakpointDown('mobile')} {
    height: ${props => `calc(100vh - ${headerHeight.mobile + props.topBarHeight}px)`};

    &::after {
      width: 90vw;
      height: 90vw;
      top: 55%;
    }
  }

  ${mediaBreakpointOnly('tablet')} {
    height: ${props => `calc(100vh - ${headerHeight.tablet + props.topBarHeight}px)`};

    &::after {
      width: 85vw;
      height: 85vw;
    }
  }
`;

const StyledNav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 100%;
`;

const StyledNavItem = styled(Link)`
  font-family: ${fontFamiliesR4.frontPageTitle};
  font-weight: ${fontWeights.medium};
  font-size: 41px; /* Not defined in typography variables */
  line-height: 125%;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${paletteR4.black.base};
  text-decoration: none;
  transition: ${transition.fast};

  &:hover {
    color: ${paletteR4.red.lowlight};
  }

  &:not(:first-child) {
    margin-top: 1em;
  }

  ${mediaBreakpointDown('mobile')} {
    font-size: 22px;
  }

  ${mediaBreakpointOnly('tablet')} {
    font-size: 32px;
  }
`;

export const OverlayMenu: FC<OverlayMenuProps> = props => {
  const {items, isVisible, topBarHeight} = props;

  return (
    <StyledWrapper isVisible={isVisible} topBarHeight={topBarHeight}>
      <StyledNav>
        {items.map(item => (
          <StyledNavItem to={item.href} key={item.name + item.href}>
            {item.name}
          </StyledNavItem>
        ))}
      </StyledNav>
    </StyledWrapper>
  );
};
